import { api, formatTenne, formatTenneCurrency, parseDecimalInput } from "@awtoban/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@awtoban/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "../../i18n";
import { bcp47, HqHeader } from "../../lib/HqHeader";

interface Company {
  id: string;
  code: string;
  name: string;
  status: "active" | "suspended";
  balance_tenne: number;
  low_balance: boolean;
}

interface LedgerEntry {
  id: number;
  kind: string;
  amount_tenne: number;
  balance_after_tenne: number;
  transaction: string | null;
  ref: string;
  at: string;
}

/** Parse a manat amount to integer teňňe WITHOUT float math; reject >2 decimals
 *  and non-positive/garbage input (returns null) rather than silently rounding. */
function manatToTenne(raw: string): number | null {
  const parsed = parseDecimalInput(raw);
  if (!parsed || parsed.startsWith("-")) return null;
  const [whole, frac = ""] = parsed.split(".");
  if (frac.length > 2) return null;
  const cents = `${frac}00`.slice(0, 2);
  const tenne = Number(whole) * 100 + Number(cents);
  return tenne > 0 ? tenne : null;
}

export function CompaniesPage() {
  const { t, i18n } = useTranslation();
  const money = bcp47(i18n.language as Locale);
  const [moneyOp, setMoneyOp] = React.useState<{
    company: Company;
    mode: "topup" | "refund";
  } | null>(null);
  const [ledgerFor, setLedgerFor] = React.useState<Company | null>(null);

  const companies = useQuery({
    queryKey: ["hq-companies"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/hq/companies", {});
      if (error || !data) throw new Error("companies_failed");
      return data as unknown as Company[];
    },
  });

  const float = useQuery({
    queryKey: ["hq-company-float"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/hq/companies/float", {});
      return data as unknown as { outstanding_balance_tenne: number; company_count: number };
    },
  });

  return (
    <div className="min-h-screen bg-paper-soft">
      <HqHeader active="companies" />
      <main className="mx-auto max-w-[1000px] space-y-6 px-4 py-4 sm:px-6 sm:py-6">
        <div className="rounded-md border border-line-strong bg-paper p-4">
          <div className="text-[11px] text-ink-muted uppercase tracking-wider">
            {t("companies.float_label")}
          </div>
          <div className="tabular mt-1 font-semibold text-2xl">
            {float.data ? formatTenneCurrency(float.data.outstanding_balance_tenne, money) : "—"}
          </div>
          <div className="mt-1 text-[12px] text-ink-muted">
            {float.data ? t("companies.float_sub", { count: float.data.company_count }) : ""}
          </div>
        </div>

        <section className="rounded-md border border-line-strong bg-paper">
          <h2 className="border-line border-b px-4 py-2 font-medium text-ink-muted text-xs uppercase tracking-wider">
            {t("companies.title")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-[14px]">
              <thead>
                <tr className="border-line border-b text-[11px] text-ink-muted uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">{t("companies.code")}</th>
                  <th className="px-4 py-2 text-left">{t("companies.name")}</th>
                  <th className="px-4 py-2 text-right">{t("companies.balance")}</th>
                  <th className="px-4 py-2 text-left">{t("companies.status")}</th>
                  <th className="px-4 py-2 text-right">{t("companies.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {companies.isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-ink-muted text-sm">
                      {t("companies.loading")}
                    </td>
                  </tr>
                ) : (companies.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-ink-muted text-sm">
                      {t("companies.empty")}
                    </td>
                  </tr>
                ) : (
                  (companies.data ?? []).map((c) => (
                    <tr key={c.id} className="border-line border-b last:border-b-0">
                      <td className="px-4 py-2 font-medium">{c.code}</td>
                      <td className="px-4 py-2">{c.name}</td>
                      <td
                        className={`tabular px-4 py-2 text-right ${c.low_balance ? "text-danger" : ""}`}
                      >
                        {formatTenne(c.balance_tenne, money)}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded-sm border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
                            c.status === "active"
                              ? "border-success/30 bg-success/10 text-success-fg"
                              : "border-danger/30 bg-danger/10 text-danger-fg"
                          }`}
                        >
                          {t(`companies.status_${c.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setMoneyOp({ company: c, mode: "topup" })}
                          >
                            {t("companies.topup")}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setMoneyOp({ company: c, mode: "refund" })}
                          >
                            {t("companies.refund")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setLedgerFor(c)}>
                            {t("companies.statement")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {moneyOp ? (
        <MoneyDialog
          key={`${moneyOp.company.id}-${moneyOp.mode}`}
          company={moneyOp.company}
          mode={moneyOp.mode}
          money={money}
          onClose={() => setMoneyOp(null)}
          onDone={() => {
            setMoneyOp(null);
            void companies.refetch();
            void float.refetch();
          }}
        />
      ) : null}

      {ledgerFor ? (
        <LedgerDialog company={ledgerFor} money={money} onClose={() => setLedgerFor(null)} />
      ) : null}
    </div>
  );
}

function MoneyDialog({
  company,
  mode,
  money,
  onClose,
  onDone,
}: {
  company: Company;
  mode: "topup" | "refund";
  money: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = React.useState("");
  const [ref, setRef] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const amountTenne = manatToTenne(amount);
  const valid = amountTenne !== null && ref.trim().length > 0;

  const submit = async () => {
    if (amountTenne === null || !ref.trim() || submitting) return;
    setSubmitting(true);
    const path =
      mode === "topup"
        ? "/api/v1/hq/companies/{company_id}/topup"
        : "/api/v1/hq/companies/{company_id}/refund";
    const { error } = await api.POST(path, {
      params: { path: { company_id: company.id } },
      body: { amount_tenne: amountTenne, ref: ref.trim() } as never,
    });
    setSubmitting(false);
    if (error) {
      toast.error(t(`companies.${mode}_error`));
      return;
    }
    toast.success(t(`companies.${mode}_done`, { name: company.name }));
    onDone();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(mode === "topup" ? "companies.topup_title" : "companies.refund_title")}
          </DialogTitle>
          <DialogDescription>
            {company.name} · {t("companies.balance")}:{" "}
            {formatTenneCurrency(company.balance_tenne, money)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="amount">{t("companies.amount_manat")}</Label>
            <Input
              id="amount"
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ref">{t("companies.ref")}</Label>
            <Input
              id="ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={t("companies.ref_hint")}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            {t("companies.cancel")}
          </Button>
          <Button disabled={!valid || submitting} onClick={() => void submit()}>
            {submitting ? t("companies.saving") : t("companies.confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LedgerDialog({
  company,
  money,
  onClose,
}: {
  company: Company;
  money: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ledger = useQuery({
    queryKey: ["hq-company-ledger", company.id],
    queryFn: async () => {
      const { data } = await api.GET("/api/v1/hq/companies/{company_id}/ledger", {
        params: { path: { company_id: company.id } },
      });
      return (data as unknown as { entries: LedgerEntry[] }).entries;
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("companies.statement_title", { name: company.name })}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-line border-b text-[11px] text-ink-muted uppercase tracking-wider">
                <th className="px-2 py-1 text-left">{t("companies.when")}</th>
                <th className="px-2 py-1 text-left">{t("companies.kind")}</th>
                <th className="px-2 py-1 text-right">{t("companies.amount")}</th>
                <th className="px-2 py-1 text-right">{t("companies.balance")}</th>
              </tr>
            </thead>
            <tbody>
              {(ledger.data ?? []).map((e) => (
                <tr key={e.id} className="border-line border-b last:border-b-0">
                  <td className="px-2 py-1 text-ink-muted">
                    {new Date(e.at).toLocaleString(money, {
                      hour12: false,
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-2 py-1">{t(`companies.kind_${e.kind}`)}</td>
                  <td
                    className={`tabular px-2 py-1 text-right ${e.amount_tenne < 0 ? "text-danger" : "text-success-fg"}`}
                  >
                    {formatTenne(e.amount_tenne, money)}
                  </td>
                  <td className="tabular px-2 py-1 text-right">
                    {formatTenne(e.balance_after_tenne, money)}
                  </td>
                </tr>
              ))}
              {ledger.data && ledger.data.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-2 py-4 text-center text-ink-muted">
                    {t("companies.statement_empty")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            {t("companies.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
